/**
 * mobile-tap-lightbox.js
 *
 * Mobile tap-to-zoom for the ProductWiz Rio product gallery, west-elm-style
 * viewer: tap the main product image to open the theme's PhotoSwipe, laid out
 * as [top: title + big +/− zoom + ✕] [middle: image or video] [bottom:
 * thumbnail strip of all media].
 *
 * WHY THIS EXISTS (2026-09-29 diagnosis, docs/mobile-image-lightbox-plan-2026-09-29.md v4):
 * Rio's own mobile lightbox (zoomType "lightBox") is click-delegated, but on
 * this store a real touch inside the gallery never produces a click event —
 * instrumented proof: pointerup reaches document, click count stays 0 (a
 * preventDefault somewhere in the touch pipeline cancels click generation).
 * So Rio's lightbox can never open from a real tap, on emulated and real
 * devices alike. This script therefore detects taps from POINTER events and
 * never relies on click.
 *
 * UI (v5): custom controls are registered through the library's official
 * `uiRegister` + `ui.registerElement()` API. This build registers its default
 * elements unconditionally (it does not honor zoom/counter options), so the
 * default small zoom button and the "1 / 9" counter are filtered out of
 * ui.uiElementsData inside uiRegister.
 *
 * VIDEO (v6): Rio renders gallery videos as a lazy div
 * ([data-tagname="VIDEO"], poster attrs) containing an iframe to the pwzcdn
 * player whose `payload=` query param carries the Shopify-hosted mp4/m3u8
 * sources and the poster URL. Video slides are appended to the dataSource as
 * PhotoSwipe html slides rendering a native <video controls>. Because the
 * iframe swallows pointer events, taps on the video area cannot reach this
 * parent-document listener — the video is reached through in-viewer
 * navigation (strip ▶ thumb, arrows, swipes), while taps on Rio's iframe keep
 * working for Rio's inline player. Videos pause on slide change and on close.
 *
 * CONTEXT: the gallery DOM is rendered by Rio at runtime (the theme's native
 * <media-gallery> is hidden) and Rio swaps nodes freely, so — like
 * hover-flyout-zoom.js — this uses document-level event delegation instead of
 * binding to nodes. Desktop keeps hover-flyout-zoom.js; this file only acts
 * within the Rio mobile breakpoint (≤ 768px).
 *
 * DEPENDENCY: window.FoxTheme.PhotoSwipe / PhotoSwipeLightbox (photoswipe.js,
 * loaded by main-product.liquid when the section's "enable_image_zoom" is on;
 * this script no-ops without it).
 *
 * REVERT: delete this file + the single <script> include next to
 * hover-flyout-zoom.js in sections/main-product.liquid + the
 * "artamore custom: mobile tap-to-zoom lightbox" block in
 * assets/custom-artamore.css. Nothing else is touched.
 */
(function () {
  'use strict';

  if (!window.FoxTheme || !window.FoxTheme.PhotoSwipe || !window.FoxTheme.PhotoSwipeLightbox) return;

  var MOBILE_MQ = '(max-width: 768px)'; // same breakpoint as the Rio gallery

  // A tap = down/up within this distance and time. Deliberate swipes move
  // much farther and must stay with the swiper.
  var MAX_MOVE_PX = 12;
  var MAX_DURATION_MS = 500;

  // The main gallery image renders at the gallery width; thumbnails are far
  // smaller. Size — not container classes — is what reliably separates them,
  // because Rio's thumbnail strip may reuse the same slide class names.
  var MIN_MAIN_IMG_PX = 150;

  // Viewer image rendition / thumbnail / video-poster widths, zoom step.
  var HIRES_WIDTH = 1946;
  var THUMB_WIDTH = 180;
  var POSTER_WIDTH = 1080;
  var ZOOM_STEP = 1.6;

  var downImg = null;
  var downX = 0;
  var downY = 0;
  var downT = 0;
  var activePointers = 0;

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function mainGalleryImg(el) {
    if (!el || el.tagName !== 'IMG') return null;
    var slide = el.closest('.pwzrswiper-slide');
    if (!slide) return null;
    return el.getBoundingClientRect().width >= MIN_MAIN_IMG_PX ? el : null;
  }

  // Swap the Shopify "width=" rendition parameter, keeping any crop params.
  function rendition(src, width) {
    if (!src) return src;
    return src.replace(/([?&])width=\d+/, '$1width=' + width);
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function slideData(img) {
    var src = img.currentSrc || img.src;
    if (!src) return null;
    var nw = img.naturalWidth || 1;
    var nh = img.naturalHeight || 1;
    return {
      src: rendition(src, HIRES_WIDTH),
      w: HIRES_WIDTH,
      h: Math.max(1, Math.round((HIRES_WIDTH * nh) / nw)),
      alt: img.alt || ''
    };
  }

  // Rio video slide: lazy div [data-tagname="VIDEO"] + pwzcdn player iframe.
  // The mp4 source and poster live in the iframe's payload= query param.
  function videoItemFrom(slide) {
    var frame = slide.querySelector('iframe[src*="payload="]');
    var lazy = slide.querySelector('[data-tagname="VIDEO"]');
    var mp4 = null;
    var poster = (lazy && (lazy.getAttribute('data-poster') || lazy.getAttribute('poster'))) || null;
    var alt = (frame && frame.getAttribute('title')) || '';
    if (frame) {
      var m = /[?&]payload=([^&]+)/.exec(frame.getAttribute('src') || '');
      if (m) {
        try {
          var payload = JSON.parse(decodeURIComponent(m[1]));
          if (payload.poster) poster = payload.poster;
          (payload.sources || []).some(function (s) {
            if (/^video\//.test(s.mimeType) && s.url) { mp4 = s.url; return true; }
            return false;
          });
        } catch (e) { /* unparsable payload → poster attrs are the fallback */ }
      }
    }
    if (!mp4) return null;
    if (mp4.indexOf('//') === 0) mp4 = 'https:' + mp4;
    return {
      isVideo: true,
      html:
        '<div class="art-lb-video-wrap">' +
        '<video class="art-lb-video" controls playsinline preload="metadata"' +
        (poster ? ' poster="' + esc(rendition(poster, POSTER_WIDTH)) + '"' : '') +
        ' src="' + esc(mp4) + '"></video></div>',
      poster: poster ? rendition(poster, THUMB_WIDTH) : null,
      alt: alt
    };
  }

  // Media of the swiper wrapper the touched slide belongs to, in DOM order:
  // image slides become image items, video slides become html <video> items.
  function dataSourceFrom(slideEl) {
    var wrapper = slideEl && slideEl.parentElement;
    if (!wrapper) return { data: [], index: 0 };
    var slides = [].filter.call(wrapper.children, function (n) {
      return n.classList && n.classList.contains('pwzrswiper-slide');
    });
    var data = [];
    var index = 0;
    for (var i = 0; i < slides.length; i++) {
      if (slides[i] === slideEl) index = data.length;
      var img = slides[i].querySelector('img');
      var item = img ? slideData(img) : videoItemFrom(slides[i]);
      if (item) data.push(item);
    }
    return { data: data, index: index };
  }

  var MAGNIFIER_PLUS =
    '<svg viewBox="0 0 32 32" width="34" height="34" aria-hidden="true">' +
    '<circle cx="14" cy="14" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/>' +
    '<path d="M20.6 20.6 27.4 27.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M9.6 14h8.8M14 9.6v8.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';
  var MAGNIFIER_MINUS =
    '<svg viewBox="0 0 32 32" width="34" height="34" aria-hidden="true">' +
    '<circle cx="14" cy="14" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/>' +
    '<path d="M20.6 20.6 27.4 27.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M9.6 14h8.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';
  var PLAY_BADGE =
    '<svg viewBox="0 0 10 12" aria-hidden="true"><path d="M0 0l10 6-10 6z"/></svg>';
  var CLOSE_X =
    '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">' +
    '<path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';
  var CHEVRON_L =
    '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">' +
    '<path d="M15 4l-8 8 8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  var CHEVRON_R =
    '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">' +
    '<path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function isVideoSlide(pswp) {
    var slide = pswp.currSlide;
    return !!(slide && slide.data && slide.data.isVideo);
  }

  function zoomBy(pswp, factor) {
    var slide = pswp.currSlide;
    if (!slide || !slide.zoomLevels || isVideoSlide(pswp)) return;
    var zl = slide.zoomLevels;
    var target = Math.min(zl.max, Math.max(zl.min, slide.currZoomLevel * factor));
    slide.zoomTo(target, { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 });
  }

  var zoomInBtn = null;
  var zoomOutBtn = null;
  function updateZoomButtons(pswp) {
    var video = isVideoSlide(pswp);
    var slide = pswp.currSlide;
    var zl = slide && slide.zoomLevels;
    if (zoomInBtn) zoomInBtn.disabled = video || !zl || slide.currZoomLevel >= zl.max - 0.001;
    if (zoomOutBtn) zoomOutBtn.disabled = video || !zl || slide.currZoomLevel <= zl.min + 0.001;
  }

  function pauseViewerVideos(pswp) {
    [].forEach.call(pswp.element.querySelectorAll('video'), function (v) {
      if (!v.paused) v.pause();
    });
  }

  var lightbox = null;
  function ensureLightbox() {
    if (lightbox) return lightbox;

    lightbox = new window.FoxTheme.PhotoSwipeLightbox({
      pswpModule: window.FoxTheme.PhotoSwipe,
      bgOpacity: 1,
      showHideAnimation: 'fade',
      preload: [1, 2],
      // The bottom thumbnail strip replaces swipe-down-to-close.
      closeOnVerticalDrag: false,
      // Reserve space for the top bar and the bottom strip so the image area
      // sits between them (west-elm layout).
      padding: { top: 104, bottom: 128, left: 0, right: 0 }
    });

    lightbox.on('uiRegister', function () {
      var pswp = lightbox.pswp;

      // This build registers its default elements unconditionally (it does
      // not honor zoom/counter options), and its skin hides the arrows on
      // touch devices (.pswp--touch) — so drop the default zoom button,
      // counter, close and arrows here; thin custom versions are registered
      // below (west-elm style, always visible on touch).
      pswp.ui.uiElementsData = pswp.ui.uiElementsData.filter(function (d) {
        return (
          d &&
          d.name !== 'zoom' &&
          d.name !== 'counter' &&
          d.name !== 'arrowPrev' &&
          d.name !== 'arrowNext' &&
          d.name !== 'close'
        );
      });

      // Title: read-only copy of the PDP H1.
      pswp.ui.registerElement({
        name: 'artTitle',
        order: 1,
        isButton: false,
        appendTo: 'bar',
        onInit: function (el) {
          el.className = 'art-lb-title';
          var h1 = document.querySelector('h1');
          el.textContent = ((h1 && h1.textContent) || '').trim();
        }
      });

      // Thin ✕ (replaces the skin's bold default close).
      pswp.ui.registerElement({
        name: 'artClose',
        order: 20,
        isButton: true,
        appendTo: 'bar',
        className: 'art-lb-close',
        title: 'Close',
        html: CLOSE_X,
        onClick: 'close'
      });

      // Thin chevron arrows, always visible (the skin hides its own on
      // touch); appended to the pswp root, vertically centered on the image
      // area via CSS.
      pswp.ui.registerElement({
        name: 'artArrowPrev',
        order: 10,
        isButton: true,
        appendTo: 'root',
        className: 'art-lb-arrow art-lb-arrow--prev',
        title: 'Previous',
        html: CHEVRON_L,
        onClick: 'prev'
      });

      pswp.ui.registerElement({
        name: 'artArrowNext',
        order: 11,
        isButton: true,
        appendTo: 'root',
        className: 'art-lb-arrow art-lb-arrow--next',
        title: 'Next',
        html: CHEVRON_R,
        onClick: 'next'
      });

      pswp.ui.registerElement({
        name: 'artZoomIn',
        order: 5,
        isButton: true,
        appendTo: 'bar',
        className: 'art-lb-zoom',
        title: 'Zoom in',
        html: MAGNIFIER_PLUS,
        onInit: function (el) { zoomInBtn = el; },
        onClick: function (ev, el, p) { zoomBy(p, ZOOM_STEP); }
      });

      pswp.ui.registerElement({
        name: 'artZoomOut',
        order: 6,
        isButton: true,
        appendTo: 'bar',
        className: 'art-lb-zoom',
        title: 'Zoom out',
        html: MAGNIFIER_MINUS,
        onInit: function (el) { zoomOutBtn = el; },
        onClick: function (ev, el, p) { zoomBy(p, 1 / ZOOM_STEP); }
      });

      pswp.on('zoomPanUpdate', function () { updateZoomButtons(pswp); });
      pswp.on('change', function () {
        updateZoomButtons(pswp);
        pauseViewerVideos(pswp);
      });
      pswp.on('destroy', function () { pauseViewerVideos(pswp); });

      // Bottom thumbnail strip: one thumb per dataSource item; video items
      // show their poster with a ▶ badge.
      pswp.ui.registerElement({
        name: 'artThumbs',
        isButton: false,
        appendTo: 'root',
        onInit: function (el, p) {
          el.className = 'art-lb-thumbs';
          var data = p.options.dataSource || [];
          data.forEach(function (item, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'art-lb-thumb' + (item.isVideo ? ' art-lb-thumb--video' : '');
            b.setAttribute(
              'aria-label',
              (item.isVideo ? 'View video ' : 'View image ') + (i + 1) + ' of ' + data.length
            );
            b.setAttribute('data-art-index', String(i));
            var im = document.createElement('img');
            im.src = item.isVideo ? item.poster : rendition(item.src, THUMB_WIDTH);
            im.alt = item.alt || '';
            im.loading = 'lazy';
            im.decoding = 'async';
            b.appendChild(im);
            if (item.isVideo) {
              var badge = document.createElement('span');
              badge.className = 'art-lb-thumb-play';
              badge.innerHTML = PLAY_BADGE;
              b.appendChild(badge);
            }
            el.appendChild(b);
          });
          el.addEventListener('click', function (ev) {
            var btn = ev.target.closest && ev.target.closest('.art-lb-thumb');
            if (btn) p.goTo(Number(btn.getAttribute('data-art-index')));
          });
          function sync() {
            var cur = p.currIndex;
            var kids = el.children;
            for (var i = 0; i < kids.length; i++) {
              var isCurrent = i === cur;
              kids[i].classList.toggle('art-lb-thumb--current', isCurrent);
              if (isCurrent && kids[i].scrollIntoView) {
                kids[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              }
            }
          }
          p.on('change', sync);
          sync();
        }
      });
    });

    return lightbox;
  }

  function openAt(img) {
    if (window.pswp) return; // a viewer is already open
    var slide = img.closest('.pwzrswiper-slide');
    if (!slide) return;
    var built = dataSourceFrom(slide);
    if (!built.data.length) return;
    ensureLightbox().loadAndOpen(built.index, built.data);
  }

  // Capture phase: fires before any child's stopPropagation could hide the
  // gesture from us. Passive: we never preventDefault — the swiper must keep
  // working for swipes.
  document.addEventListener(
    'pointerdown',
    function (ev) {
      activePointers++;
      if (activePointers > 1) {
        downImg = null; // pinch / second finger — not a tap
        return;
      }
      downImg = mainGalleryImg(ev.target);
      downX = ev.clientX;
      downY = ev.clientY;
      downT = ev.timeStamp;
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointerup',
    function (ev) {
      activePointers = Math.max(0, activePointers - 1);
      var img = downImg;
      downImg = null;
      if (!img || !isMobile() || window.pswp) return;
      if (ev.timeStamp - downT > MAX_DURATION_MS) return;
      if (Math.abs(ev.clientX - downX) > MAX_MOVE_PX) return;
      if (Math.abs(ev.clientY - downY) > MAX_MOVE_PX) return;
      openAt(img);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointercancel',
    function () {
      activePointers = 0;
      downImg = null; // scroll / gesture took over — not a tap
    },
    { capture: true, passive: true }
  );
})();
